import { useAppStore } from '@/store/useAppStore';
import { classifyGpaMatch, matchToShortlistBand } from '@/utils/gpaMatch';
import { StarIcon as Star } from './ui/Icons';

const BAND_STYLES: Record<string, string> = {
  reach: 'bg-warning-light text-warning',
  match: 'bg-info-light text-info',
  safety: 'bg-success-light text-success',
};

const SOURCE_LABEL: Record<string, string> = {
  uc: 'UC',
  commonapp: 'Common App',
};

export function CollegeShortlist() {
  const result = useAppStore((s) => s.result);
  const collegeShortlist = useAppStore((s) => s.collegeShortlist);
  const removeFromShortlist = useAppStore((s) => s.removeFromShortlist);
  const clearShortlist = useAppStore((s) => s.clearShortlist);

  if (collegeShortlist.length === 0) {
    return (
      <div
        className="rounded-lg border border-border bg-surface p-4"
        data-testid="college-shortlist"
      >
        <h3 className="mb-1 text-sm font-semibold text-[var(--color-text)]">College Shortlist</h3>
        <p className="text-xs text-[var(--color-text-muted)]">
          Tap the ★ icon next to any campus or institution in the tables above to build your reach / match / safety list here.
        </p>
      </div>
    );
  }

  // The comparison GPA differs by source: UC uses UC Capped, Common App uses Unweighted.
  const ucCappedGPA =
    result?.variants.find((v) => v.label === 'UC Capped Weighted GPA')?.value ?? null;
  const unweightedGPA =
    result?.variants.find((v) => v.label === 'Unweighted GPA')?.value ?? null;

  const counts = { reach: 0, match: 0, safety: 0 } as Record<string, number>;

  const rows = collegeShortlist.map((entry) => {
    const studentGPA = entry.source === 'uc' ? ucCappedGPA : unweightedGPA;
    const band = matchToShortlistBand(classifyGpaMatch(studentGPA, entry.range));
    counts[band] += 1;
    return { entry, band };
  });

  return (
    <div
      className="rounded-lg border border-border bg-surface p-4"
      data-testid="college-shortlist"
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--color-text)]">College Shortlist</h3>
        <button
          type="button"
          onClick={clearShortlist}
          className="rounded px-2 py-1 text-xs text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text)]"
          data-testid="shortlist-clear"
        >
          Clear all
        </button>
      </div>
      <p className="mb-3 text-xs text-[var(--color-text-muted)]" data-testid="shortlist-summary">
        {counts.reach} reach{counts.reach === 1 ? '' : 'es'} · {counts.match} match{counts.match === 1 ? '' : 'es'} · {counts.safety} safet{counts.safety === 1 ? 'y' : 'ies'}
      </p>
      <ul className="space-y-1.5">
        {rows.map(({ entry, band }) => (
          <li
            key={`${entry.source}-${entry.key}`}
            className="flex items-center justify-between rounded border border-border p-2 text-xs"
            data-testid={`shortlist-row-${entry.key}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <Star className="h-3 w-3 fill-warning text-warning" aria-hidden="true" />
                <span className="truncate font-medium text-[var(--color-text)]">{entry.name}</span>
              </div>
              <div className="text-[10px] text-[var(--color-text-muted)]">
                {SOURCE_LABEL[entry.source]} · {entry.scale} scale
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${BAND_STYLES[band]}`}
                data-testid={`shortlist-band-${entry.key}`}
              >
                {band}
              </span>
              <button
                type="button"
                onClick={() => removeFromShortlist(entry.key)}
                className="rounded p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text)]"
                aria-label={`Remove ${entry.name} from shortlist`}
                data-testid={`shortlist-remove-${entry.key}`}
              >
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
